import React, { createContext, useContext, useState, useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// Create the context
const SnackbarContext = createContext();

// Custom hook to use the snackbar context
export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within a SnackbarProvider");
  }
  return context;
};

// Snackbar Provider Component
export const SnackbarProvider = ({ children }) => {
  const [snackbars, setSnackbars] = useState([]);

  // Function to show a snackbar
  const showSnackbar = useCallback(
    (message, severity = "info", options = {}) => {
      const {
        autoHideDuration = 6000,
        persistent = false,
        action = null,
        onClose = null,
      } = options;

      const id = Date.now() + Math.random();
      const newSnackbar = {
        id,
        message,
        severity,
        autoHideDuration: persistent ? null : autoHideDuration,
        action,
        onClose,
        open: true,
      };

      setSnackbars((prev) => [...prev, newSnackbar]);
      return id;
    },
    []
  );

  // Function to hide a specific snackbar
  const hideSnackbar = useCallback((id) => {
    setSnackbars((prev) =>
      prev.map((snackbar) =>
        snackbar.id === id ? { ...snackbar, open: false } : snackbar
      )
    );

    // Remove from state after animation
    setTimeout(() => {
      setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
    }, 500);
  }, []);

  // Function to hide all snackbars
  const hideAllSnackbars = useCallback(() => {
    setSnackbars((prev) =>
      prev.map((snackbar) => ({ ...snackbar, open: false }))
    );

    // Remove from state after animation
    setTimeout(() => {
      setSnackbars([]);
    }, 500);
  }, []);

  // Convenience methods for different severities
  const showSuccess = useCallback(
    (message, options = {}) => {
      return showSnackbar(message, "success", options);
    },
    [showSnackbar]
  );

  const showError = useCallback(
    (message, options = {}) => {
      return showSnackbar(message, "error", options);
    },
    [showSnackbar]
  );

  const showWarning = useCallback(
    (message, options = {}) => {
      return showSnackbar(message, "warning", options);
    },
    [showSnackbar]
  );

  const showInfo = useCallback(
    (message, options = {}) => {
      return showSnackbar(message, "info", options);
    },
    [showSnackbar]
  );

  // Handle snackbar close
  const handleClose = useCallback(
    (id, reason, onCloseCallback) => {
      if (reason === "clickaway") {
        return;
      }

      if (onCloseCallback) {
        onCloseCallback();
      }

      hideSnackbar(id);
    },
    [hideSnackbar]
  );

  const contextValue = {
    showSnackbar,
    hideSnackbar,
    hideAllSnackbars,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}

      {/* Render all snackbars */}
      {snackbars.map((snackbar, index) => (
        <Snackbar
          key={snackbar.id}
          open={snackbar.open}
          autoHideDuration={snackbar.autoHideDuration}
          onClose={(event, reason) =>
            handleClose(snackbar.id, reason, snackbar.onClose)
          }
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          sx={{
            // Stack multiple snackbars vertically
            bottom: index * 70 + 20,
          }}
        >
          <Alert
            onClose={() => hideSnackbar(snackbar.id)}
            severity={snackbar.severity}
            variant="filled"
            action={
              snackbar.action || (
                <IconButton
                  size="small"
                  aria-label="close"
                  color="inherit"
                  onClick={() => hideSnackbar(snackbar.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }
            sx={{
              width: "100%",
              "& .MuiAlert-action": {
                alignItems: "center",
              },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      ))}
    </SnackbarContext.Provider>
  );
};

export default SnackbarContext;
